import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Typography,
  Spin,
  Space,
  Row,
  Col,
  Badge,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import videoService from "../../../services/videoService";
import { useCommon } from "../../../context/CommonContext";
import { noWhitespaceValidator } from "../../../utils/commonFunctions";

const { Title } = Typography;
const { Option } = Select;

const EditQuality = () => {
  const { vq_id } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const fetchQualityDetails = useCallback(async () => {
    if (!vq_id) {
      notify.error({ message: "Quality ID is required" });
      navigate("/video-quality");
      return;
    }

    setLoadingData(true);
    try {
      const response = await videoService.getSingleVideoQuality(vq_id);

      if (response.data && response.data.status) {
        const qualityData = response.data.data?.[0] || response.data.data;
        if (qualityData && qualityData.vq_id) {
          form.setFieldsValue({
            quality_name: qualityData.quality_name,
            height: qualityData.height,
            width: qualityData.width,
            bitrate: qualityData.bitrate,
            vq_is_active: qualityData.vq_is_active || "Y",
          });
        } else {
          notify.error({ message: "Quality data not found" });
          navigate("/video-quality");
        }
      } else {
        notify.error({
          message: response.data?.message || "Failed to fetch quality details",
        });
        navigate("/video-quality");
      }
    } catch (error) {
      console.error("Error fetching quality details:", error);
      notify.error({ message: "Error fetching quality details" });
      navigate("/video-quality");
    } finally {
      setLoadingData(false);
    }
  }, [vq_id, form, navigate]);

  useEffect(() => {
    setPageTitle("Edit Video Quality");
    fetchQualityDetails();
  }, [fetchQualityDetails, setPageTitle]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        vq_id: vq_id,
      };

      const response = await videoService.editVideoQuality(vq_id, payload);

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Quality updated successfully",
        });
        navigate("/video-quality");
      } else {
        notify.error({
          message: response.data?.message || "Failed to update quality",
        });
      }
    } catch (error) {
      console.error("Error updating quality:", error);
      notify.error({
        message: error?.data?.message || "Error updating quality",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue({ vq_is_active: "Y" });
  };

  const handleGoBack = () => {
    navigate("/video-quality");
  };

  return (
    <div className="min-h-screen">
      <Spin spinning={loadingData}>
        <Card
          className="shadow-lg border-0"
          bodyStyle={{
            padding: "32px",
            backgroundColor: "#ffffff",
            borderColor: "#d9d9d9",
          }}
        >
          <Form
            form={form}
            layout="vertical"
            size="large"
            onFinish={handleSubmit}
            initialValues={{ vq_is_active: "Y" }}
            className="space-y-6"
          >
            {/* Quality Information Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <LineChartOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Quality Information
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Quality Name
                      </span>
                    }
                    name="quality_name"
                    rules={[
                      { required: true, message: "Please enter quality name" },
                      { validator: noWhitespaceValidator },
                    ]}
                  >
                    <Input
                      placeholder="Enter quality name"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Height
                      </span>
                    }
                    name="height"
                    rules={[{ required: true, message: "Please enter height" }]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      placeholder="Enter height"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Width
                      </span>
                    }
                    name="width"
                    rules={[{ required: true, message: "Please enter width" }]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      placeholder="Enter width"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Bitrate
                      </span>
                    }
                    name="bitrate"
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      placeholder="Enter bitrate (optional)"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Status
                      </span>
                    }
                    name="vq_is_active"
                  >
                    <Select className="rounded-lg">
                      <Option value="Y">
                        <Badge status="success" text="Active" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="Inactive" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Action Buttons */}
            <div
              className="flex justify-end gap-4 pt-6 border-t"
              style={{ borderColor: "#d9d9d9" }}
            >
              <Space size="middle">
                <Button
                  onClick={handleReset}
                  icon={<ReloadOutlined />}
                  size="large"
                  className="px-6"
                >
                  Reset
                </Button>
                <Button
                  onClick={handleGoBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                  className="px-8"
                >
                  {loading ? "Updating..." : "Update Quality"}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

export default EditQuality;
